import { authHeaders, jsonAuthHeaders } from '../../shared/authHeaders';

export interface SourceRecordingSegmentInput {
  roomCode: string;
  uploadId: string;
  dedupeKey: string;
  cameraId: string;
  cameraLabel: string;
  poseKey: string;
  poseName: string | null;
  poseIndex: number | null;
  contentType: string;
  startedAt: string;
}

export function getSourceRecordingDedupeKey(params: {
  roomCode: string;
  poseKey: string;
  cameraId: string;
  sourceId: string;
}) {
  return [
    params.roomCode,
    params.poseKey,
    params.cameraId,
    params.sourceId,
  ].join(':');
}

export interface QueuedSourceRecordingSegment extends SourceRecordingSegmentInput {
  status: 'recording' | 'stopped' | 'uploading' | 'failed';
  expectedChunks: number;
  uploadedChunks: number;
  uploadedBytes?: number;
  durationSeconds: number | null;
  endedAt: string | null;
  lastChunkAt: string | null;
  errorMessage: string | null;
  createdAt: number;
  updatedAt: number;
}

interface QueuedSourceRecordingChunk {
  id: string;
  roomCode: string;
  uploadId: string;
  index: number;
  blob: Blob;
  sizeBytes: number;
  createdAt: number;
}

export interface SourceRecordingUploadProgress {
  hasOutstandingWork: boolean;
  totalSegments: number;
  uploadedSegments: number;
  recordingSegments: number;
  stoppedSegments: number;
  uploadingSegments: number;
  failedSegments: number;
  totalChunks: number;
  uploadedChunks: number;
  queuedChunks: number;
  totalBytes: number;
  queuedBytes: number;
  uploadedBytes: number;
  activeUploadBytes: number;
  activeUploadTotalBytes: number;
}

export const EMPTY_SOURCE_RECORDING_UPLOAD_PROGRESS: SourceRecordingUploadProgress = {
  hasOutstandingWork: false,
  totalSegments: 0,
  uploadedSegments: 0,
  recordingSegments: 0,
  stoppedSegments: 0,
  uploadingSegments: 0,
  failedSegments: 0,
  totalChunks: 0,
  uploadedChunks: 0,
  queuedChunks: 0,
  totalBytes: 0,
  queuedBytes: 0,
  uploadedBytes: 0,
  activeUploadBytes: 0,
  activeUploadTotalBytes: 0,
};

interface UploadQueuedSourceRecordingsOptions {
  roomCode: string;
  getToken: (options?: { skipCache?: boolean }) => Promise<string | null>;
  uploadPhase: 'break' | 'session_end' | 'reference_pack';
  shouldUpload?: () => boolean;
  finalizeOpenSegments?: boolean;
  signal?: AbortSignal;
  onProgress?: (progress: SourceRecordingUploadProgress) => void;
}

interface UploadResponse {
  ok: boolean;
  status: number;
  text: string;
}

const DB_NAME = 'modelcast-source-recordings';
const SEGMENTS_STORE = 'segments';
const CHUNKS_STORE = 'chunks';
const DB_VERSION = 1;
const SOURCE_RECORDING_UPLOAD_LOCK_TTL_MS = 120_000;
const SOURCE_RECORDING_UPLOAD_LOCK_HEARTBEAT_MS = 15_000;

type BrowserLockManager = {
  request<T>(
    name: string,
    options: { ifAvailable: true },
    callback: (lock: unknown | null) => T | Promise<T>
  ): Promise<T>;
};

interface SourceRecordingUploadLock {
  owner: string;
  expiresAt: number;
}

function sourceRecordingStorageAvailable() {
  return typeof window !== 'undefined' && 'indexedDB' in window;
}

function getSourceRecordingUploadLockName(roomCode: string) {
  return `drawclub-source-recordings:${roomCode}`;
}

function getSourceRecordingUploadLockStorageKey(roomCode: string) {
  return `drawclub.sourceRecording.uploadLock:${roomCode}`;
}

function readSourceRecordingUploadLock(key: string): SourceRecordingUploadLock | null {
  try {
    const value = window.localStorage.getItem(key);
    if (!value) return null;
    const lock = JSON.parse(value) as Partial<SourceRecordingUploadLock>;
    if (typeof lock.owner !== 'string' || typeof lock.expiresAt !== 'number') return null;
    return { owner: lock.owner, expiresAt: lock.expiresAt };
  } catch {
    return null;
  }
}

function writeSourceRecordingUploadLock(key: string, owner: string) {
  window.localStorage.setItem(key, JSON.stringify({
    owner,
    expiresAt: Date.now() + SOURCE_RECORDING_UPLOAD_LOCK_TTL_MS,
  }));
}

async function withSourceRecordingUploadLock<T>(
  roomCode: string,
  whenUnavailable: () => Promise<T>,
  run: () => Promise<T>
): Promise<T> {
  const locks = (navigator as Navigator & { locks?: BrowserLockManager }).locks;
  if (locks) {
    return locks.request(getSourceRecordingUploadLockName(roomCode), { ifAvailable: true }, async (lock) => {
      if (!lock) return whenUnavailable();
      return run();
    });
  }

  let storageKey = '';
  let owner = '';
  let heartbeat: number | undefined;

  try {
    storageKey = getSourceRecordingUploadLockStorageKey(roomCode);
    owner = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
    const existingLock = readSourceRecordingUploadLock(storageKey);
    if (existingLock && existingLock.expiresAt > Date.now()) return whenUnavailable();

    writeSourceRecordingUploadLock(storageKey, owner);
    if (readSourceRecordingUploadLock(storageKey)?.owner !== owner) return whenUnavailable();

    heartbeat = window.setInterval(() => {
      writeSourceRecordingUploadLock(storageKey, owner);
    }, SOURCE_RECORDING_UPLOAD_LOCK_HEARTBEAT_MS);
  } catch {
    return run();
  }

  try {
    return await run();
  } finally {
    if (heartbeat !== undefined) {
      window.clearInterval(heartbeat);
    }
    if (readSourceRecordingUploadLock(storageKey)?.owner === owner) {
      window.localStorage.removeItem(storageKey);
    }
  }
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function transactionDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;

      if (!db.objectStoreNames.contains(SEGMENTS_STORE)) {
        const store = db.createObjectStore(SEGMENTS_STORE, { keyPath: 'uploadId' });
        store.createIndex('roomCode', 'roomCode', { unique: false });
        store.createIndex('dedupeKey', 'dedupeKey', { unique: false });
        store.createIndex('createdAt', 'createdAt', { unique: false });
      }

      if (!db.objectStoreNames.contains(CHUNKS_STORE)) {
        const store = db.createObjectStore(CHUNKS_STORE, { keyPath: 'id' });
        store.createIndex('roomCode', 'roomCode', { unique: false });
        store.createIndex('uploadId', 'uploadId', { unique: false });
        store.createIndex('createdAt', 'createdAt', { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function getAllSegments(): Promise<QueuedSourceRecordingSegment[]> {
  if (!sourceRecordingStorageAvailable()) return [];

  const db = await openDb();
  try {
    const tx = db.transaction(SEGMENTS_STORE, 'readonly');
    const segments = await requestToPromise<QueuedSourceRecordingSegment[]>(
      tx.objectStore(SEGMENTS_STORE).getAll()
    );
    await transactionDone(tx);
    return segments;
  } finally {
    db.close();
  }
}

async function getSegment(uploadId: string): Promise<QueuedSourceRecordingSegment | null> {
  if (!sourceRecordingStorageAvailable()) return null;

  const db = await openDb();
  try {
    const tx = db.transaction(SEGMENTS_STORE, 'readonly');
    const segment = await requestToPromise<QueuedSourceRecordingSegment | undefined>(
      tx.objectStore(SEGMENTS_STORE).get(uploadId)
    );
    await transactionDone(tx);
    return segment ?? null;
  } finally {
    db.close();
  }
}

async function putSegment(segment: QueuedSourceRecordingSegment): Promise<void> {
  const db = await openDb();
  try {
    const tx = db.transaction(SEGMENTS_STORE, 'readwrite');
    tx.objectStore(SEGMENTS_STORE).put({ ...segment, updatedAt: Date.now() });
    await transactionDone(tx);
  } finally {
    db.close();
  }
}

async function deleteSegment(uploadId: string): Promise<void> {
  const chunks = await getChunksForUpload(uploadId);
  const db = await openDb();

  try {
    const tx = db.transaction([SEGMENTS_STORE, CHUNKS_STORE], 'readwrite');
    tx.objectStore(SEGMENTS_STORE).delete(uploadId);
    for (const chunk of chunks) {
      tx.objectStore(CHUNKS_STORE).delete(chunk.id);
    }
    await transactionDone(tx);
  } finally {
    db.close();
  }
}

async function getChunksForUpload(uploadId: string): Promise<QueuedSourceRecordingChunk[]> {
  if (!sourceRecordingStorageAvailable()) return [];

  const db = await openDb();
  try {
    const tx = db.transaction(CHUNKS_STORE, 'readonly');
    const chunks = await requestToPromise<QueuedSourceRecordingChunk[]>(
      tx.objectStore(CHUNKS_STORE).index('uploadId').getAll(uploadId)
    );
    await transactionDone(tx);
    return chunks.sort((a, b) => a.index - b.index);
  } finally {
    db.close();
  }
}

async function putChunk(chunk: QueuedSourceRecordingChunk): Promise<void> {
  const db = await openDb();
  try {
    const tx = db.transaction(CHUNKS_STORE, 'readwrite');
    tx.objectStore(CHUNKS_STORE).put(chunk);
    await transactionDone(tx);
  } finally {
    db.close();
  }
}

function getSegmentEndedAt(segment: QueuedSourceRecordingSegment) {
  return segment.endedAt ?? segment.lastChunkAt ?? new Date(segment.updatedAt).toISOString();
}

function getSegmentDurationSeconds(segment: QueuedSourceRecordingSegment) {
  if (segment.durationSeconds !== null) return segment.durationSeconds;

  const startedAt = Date.parse(segment.startedAt);
  const endedAt = Date.parse(getSegmentEndedAt(segment));
  if (!Number.isFinite(startedAt) || !Number.isFinite(endedAt)) return null;
  return Math.max(0, Math.round((endedAt - startedAt) / 1000));
}

export function sourceRecordingProgressChanged(
  current: SourceRecordingUploadProgress,
  next: SourceRecordingUploadProgress
) {
  return Object.keys(next).some((key) => (
    next[key as keyof SourceRecordingUploadProgress] !== current[key as keyof SourceRecordingUploadProgress]
  ));
}

export async function requestPersistentSourceRecordingStorage(): Promise<void> {
  if (!('storage' in navigator) || !navigator.storage.persist) return;

  try {
    const alreadyPersisted = await navigator.storage.persisted?.();
    if (!alreadyPersisted) {
      await navigator.storage.persist();
    }
  } catch (error) {
    console.warn('[SourceRecordingUploadQueue] Persistent storage request failed', { error });
  }
}

export async function createSourceRecordingSegment(
  input: SourceRecordingSegmentInput
): Promise<{ segment: QueuedSourceRecordingSegment; created: boolean }> {
  if (!sourceRecordingStorageAvailable()) {
    throw new Error('IndexedDB is unavailable; source recording cannot be queued');
  }

  await requestPersistentSourceRecordingStorage();

  const existing = (await getAllSegments()).find((segment) =>
    segment.roomCode === input.roomCode &&
    segment.dedupeKey === input.dedupeKey
  );

  if (existing) {
    return { segment: existing, created: false };
  }

  const now = Date.now();
  const segment: QueuedSourceRecordingSegment = {
    ...input,
    status: 'recording',
    expectedChunks: 0,
    uploadedChunks: 0,
    uploadedBytes: 0,
    durationSeconds: null,
    endedAt: null,
    lastChunkAt: null,
    errorMessage: null,
    createdAt: now,
    updatedAt: now,
  };

  await putSegment(segment);
  return { segment, created: true };
}

export async function appendSourceRecordingChunk(params: {
  roomCode: string;
  uploadId: string;
  index: number;
  blob: Blob;
  capturedAt: number;
}): Promise<void> {
  if (!sourceRecordingStorageAvailable()) return;

  const segment = await getSegment(params.uploadId);
  if (!segment) {
    console.warn('[SourceRecordingUploadQueue] Segment missing while appending chunk', {
      roomCode: params.roomCode,
      uploadId: params.uploadId,
      chunkIndex: params.index,
    });
    return;
  }

  await putChunk({
    id: `${params.uploadId}:${String(params.index).padStart(6, '0')}`,
    roomCode: params.roomCode,
    uploadId: params.uploadId,
    index: params.index,
    blob: params.blob,
    sizeBytes: params.blob.size,
    createdAt: params.capturedAt,
  });

  await putSegment({
    ...segment,
    expectedChunks: Math.max(segment.expectedChunks, params.index + 1),
    lastChunkAt: new Date(params.capturedAt).toISOString(),
    errorMessage: null,
  });
}

export async function markSourceRecordingSegmentStopped(params: {
  uploadId: string;
  stoppedAt: number;
}): Promise<void> {
  const segment = await getSegment(params.uploadId);
  if (!segment) return;

  const stoppedAtIso = new Date(params.stoppedAt).toISOString();
  const startedAt = Date.parse(segment.startedAt);
  const durationSeconds = Number.isFinite(startedAt)
    ? Math.max(0, Math.round((params.stoppedAt - startedAt) / 1000))
    : null;

  await putSegment({
    ...segment,
    status: 'stopped',
    endedAt: stoppedAtIso,
    lastChunkAt: segment.lastChunkAt ?? stoppedAtIso,
    durationSeconds,
    errorMessage: null,
  });
}

export async function getQueuedSourceRecordingProgress(roomCode: string): Promise<SourceRecordingUploadProgress> {
  if (!sourceRecordingStorageAvailable()) return EMPTY_SOURCE_RECORDING_UPLOAD_PROGRESS;

  const [segments, allChunks] = await Promise.all([
    getAllSegments(),
    getAllChunks(),
  ]);
  const roomSegments = segments.filter((segment) => segment.roomCode === roomCode);
  const roomUploadIds = new Set(roomSegments.map((segment) => segment.uploadId));
  const roomChunks = allChunks.filter((chunk) => roomUploadIds.has(chunk.uploadId));
  const roomSegmentsByUploadId = new Map(roomSegments.map((segment) => [segment.uploadId, segment]));
  const queuedChunks = roomChunks.filter((chunk) => {
    const segment = roomSegmentsByUploadId.get(chunk.uploadId);
    if (!segment) return false;
    return chunk.index >= segment.uploadedChunks;
  });

  let totalChunks = 0;
  let uploadedChunks = 0;
  let uploadedBytes = 0;
  let recordingSegments = 0;
  let stoppedSegments = 0;
  let uploadingSegments = 0;
  let failedSegments = 0;

  for (const segment of roomSegments) {
    if (segment.status === 'recording') recordingSegments += 1;
    if (segment.status === 'stopped') stoppedSegments += 1;
    if (segment.status === 'uploading') uploadingSegments += 1;
    if (segment.status === 'failed' || segment.errorMessage) failedSegments += 1;
    totalChunks += Math.max(segment.expectedChunks, segment.uploadedChunks);
    uploadedChunks += segment.uploadedChunks;
    uploadedBytes += segment.uploadedBytes ?? 0;
  }

  const queuedBytes = queuedChunks.reduce((total, chunk) => total + chunk.sizeBytes, 0);

  return {
    hasOutstandingWork: roomSegments.length > 0,
    totalSegments: roomSegments.length,
    uploadedSegments: 0,
    recordingSegments,
    stoppedSegments,
    uploadingSegments,
    failedSegments,
    totalChunks,
    uploadedChunks,
    queuedChunks: queuedChunks.length,
    totalBytes: uploadedBytes + queuedBytes,
    queuedBytes,
    uploadedBytes,
    activeUploadBytes: 0,
    activeUploadTotalBytes: 0,
  };
}

async function getAllChunks(): Promise<QueuedSourceRecordingChunk[]> {
  if (!sourceRecordingStorageAvailable()) return [];

  const db = await openDb();
  try {
    const tx = db.transaction(CHUNKS_STORE, 'readonly');
    const chunks = await requestToPromise<QueuedSourceRecordingChunk[]>(
      tx.objectStore(CHUNKS_STORE).getAll()
    );
    await transactionDone(tx);
    return chunks;
  } finally {
    db.close();
  }
}

async function getUploadErrorMessage(response: Response, fallback: string): Promise<string> {
  const text = await response.text().catch(() => '');
  let detail = text.trim();

  if (detail) {
    try {
      const body = JSON.parse(detail) as { error?: string };
      detail = body.error?.trim() || detail;
    } catch {
      // Keep the raw response text.
    }
  }

  return detail ? `${fallback} (${response.status}): ${detail}` : `${fallback} (${response.status})`;
}

function getUploadResponseErrorMessage(response: UploadResponse, fallback: string): string {
  let detail = response.text.trim();

  if (detail) {
    try {
      const body = JSON.parse(detail) as { error?: string };
      detail = body.error?.trim() || detail;
    } catch {
      // Keep the raw response text.
    }
  }

  return detail ? `${fallback} (${response.status}): ${detail}` : `${fallback} (${response.status})`;
}

function uploadFormDataWithProgress(params: {
  url: string;
  formData: FormData;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  onUploadProgress?: (loadedBytes: number) => void;
}): Promise<UploadResponse> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    const handleAbort = () => {
      xhr.abort();
      reject(new DOMException('Upload aborted', 'AbortError'));
    };

    if (params.signal?.aborted) {
      handleAbort();
      return;
    }

    xhr.open('POST', params.url);
    Object.entries(params.headers ?? {}).forEach(([name, value]) => {
      xhr.setRequestHeader(name, value);
    });

    xhr.upload.onprogress = (event) => {
      params.onUploadProgress?.(event.loaded);
    };

    xhr.onload = () => {
      params.signal?.removeEventListener('abort', handleAbort);
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        text: xhr.responseText ?? '',
      });
    };

    xhr.onerror = () => {
      params.signal?.removeEventListener('abort', handleAbort);
      reject(new Error('Network error while uploading source recording chunk'));
    };

    xhr.onabort = () => {
      params.signal?.removeEventListener('abort', handleAbort);
      reject(new DOMException('Upload aborted', 'AbortError'));
    };

    params.signal?.addEventListener('abort', handleAbort, { once: true });
    xhr.send(params.formData);
  });
}

async function uploadChunk(params: {
  roomCode: string;
  getToken: UploadQueuedSourceRecordingsOptions['getToken'];
  uploadId: string;
  chunk: QueuedSourceRecordingChunk;
  signal?: AbortSignal;
  onUploadProgress?: (loadedBytes: number) => void;
}) {
  const send = async (skipCache = false) => {
    const token = await params.getToken(skipCache ? { skipCache: true } : undefined);

    const formData = new FormData();
    formData.append('chunkIndex', String(params.chunk.index));
    const uploadBlob = new Blob([params.chunk.blob], { type: 'application/octet-stream' });
    formData.append('chunk', uploadBlob, `chunk-${String(params.chunk.index).padStart(6, '0')}.part`);

    return uploadFormDataWithProgress({
      url: `/api/recordings/${params.roomCode}/source-recordings/${params.uploadId}/chunks`,
      headers: authHeaders(token),
      formData,
      signal: params.signal,
      onUploadProgress: params.onUploadProgress,
    });
  };

  let response = await send();
  if (response.status === 401) {
    response = await send(true);
  }

  if (!response.ok) {
    throw new Error(getUploadResponseErrorMessage(response, `Failed to upload source recording chunk ${params.chunk.index}`));
  }
}

async function finalizeSegment(params: {
  roomCode: string;
  getToken: UploadQueuedSourceRecordingsOptions['getToken'];
  segment: QueuedSourceRecordingSegment;
  expectedChunks: number;
  signal?: AbortSignal;
}) {
  const body = JSON.stringify({
    expectedChunks: params.expectedChunks,
    contentType: params.segment.contentType || 'video/webm',
    durationSeconds: getSegmentDurationSeconds(params.segment),
    cameraId: params.segment.cameraId,
    cameraLabel: params.segment.cameraLabel,
    poseName: params.segment.poseName,
    poseIndex: params.segment.poseIndex,
    startedAt: params.segment.startedAt,
    endedAt: getSegmentEndedAt(params.segment),
  });

  const send = async (skipCache = false) => {
    const token = await params.getToken(skipCache ? { skipCache: true } : undefined);

    return fetch(`/api/recordings/${params.roomCode}/source-recordings/${params.segment.uploadId}/finalize`, {
      method: 'POST',
      headers: jsonAuthHeaders(token),
      body,
      signal: params.signal,
    });
  };

  let response = await send();
  if (response.status === 401) {
    response = await send(true);
  }

  if (!response.ok) {
    throw new Error(await getUploadErrorMessage(response, 'Failed to finalize source recording'));
  }
}

export async function uploadQueuedSourceRecordings({
  roomCode,
  getToken,
  uploadPhase,
  shouldUpload = () => true,
  finalizeOpenSegments = false,
  signal,
  onProgress,
}: UploadQueuedSourceRecordingsOptions): Promise<SourceRecordingUploadProgress> {
  if (!sourceRecordingStorageAvailable()) return EMPTY_SOURCE_RECORDING_UPLOAD_PROGRESS;
  if (signal?.aborted || !shouldUpload()) return getQueuedSourceRecordingProgress(roomCode);

  return withSourceRecordingUploadLock(
    roomCode,
    () => getQueuedSourceRecordingProgress(roomCode),
    () => uploadQueuedSourceRecordingsUnlocked({
      roomCode,
      getToken,
      uploadPhase,
      shouldUpload,
      finalizeOpenSegments,
      signal,
      onProgress,
    })
  );
}

async function uploadQueuedSourceRecordingsUnlocked({
  roomCode,
  getToken,
  uploadPhase,
  shouldUpload = () => true,
  finalizeOpenSegments = false,
  signal,
  onProgress,
}: UploadQueuedSourceRecordingsOptions): Promise<SourceRecordingUploadProgress> {
  const segments = (await getAllSegments())
    .filter((segment) => segment.roomCode === roomCode)
    .sort((a, b) => a.createdAt - b.createdAt);
  let uploadedSegments = 0;

  for (const initialSegment of segments) {
    if (signal?.aborted || !shouldUpload()) break;

    const currentSegment = await getSegment(initialSegment.uploadId);
    if (!currentSegment) continue;

    if (currentSegment.status === 'recording' && !finalizeOpenSegments) {
      continue;
    }

    const allChunks = await getChunksForUpload(currentSegment.uploadId);
    const chunks = allChunks.filter((chunk) => chunk.index >= currentSegment.uploadedChunks);
    const expectedChunks = Math.max(
      currentSegment.expectedChunks,
      allChunks.length,
      currentSegment.uploadedChunks
    );

    if (expectedChunks === 0) {
      await deleteSegment(currentSegment.uploadId);
      onProgress?.(await getQueuedSourceRecordingProgress(roomCode));
      continue;
    }

    await putSegment({
      ...currentSegment,
      status: 'uploading',
      expectedChunks,
      errorMessage: null,
      endedAt: currentSegment.endedAt ?? (finalizeOpenSegments ? getSegmentEndedAt(currentSegment) : null),
      durationSeconds: currentSegment.durationSeconds ?? (finalizeOpenSegments ? getSegmentDurationSeconds(currentSegment) : null),
    });
    onProgress?.(await getQueuedSourceRecordingProgress(roomCode));

    let segment = await getSegment(currentSegment.uploadId);
    if (!segment) continue;

    try {
      for (const chunk of chunks) {
        if (signal?.aborted || !shouldUpload()) {
          await putSegment({ ...segment, status: currentSegment.status === 'recording' ? 'recording' : 'stopped' });
          return getQueuedSourceRecordingProgress(roomCode);
        }

        const progressBeforeChunk = await getQueuedSourceRecordingProgress(roomCode);
        await uploadChunk({
          roomCode,
          getToken,
          uploadId: segment.uploadId,
          chunk,
          signal,
          onUploadProgress: (loadedBytes) => {
            onProgress?.({
              ...progressBeforeChunk,
              uploadedSegments,
              activeUploadBytes: Math.min(chunk.sizeBytes, loadedBytes),
              activeUploadTotalBytes: chunk.sizeBytes,
            });
          },
        });

        segment = {
          ...segment,
          uploadedChunks: Math.max(segment.uploadedChunks, chunk.index + 1),
          uploadedBytes: (segment.uploadedBytes ?? 0) + chunk.sizeBytes,
          expectedChunks: Math.max(segment.expectedChunks, expectedChunks),
          errorMessage: null,
        };
        await putSegment(segment);
        onProgress?.(await getQueuedSourceRecordingProgress(roomCode));
      }

      if (segment.uploadedChunks >= expectedChunks) {
        await finalizeSegment({
          roomCode,
          getToken,
          segment,
          expectedChunks,
          signal,
        });
        await deleteSegment(segment.uploadId);
        uploadedSegments++;
        console.log('[SourceRecordingUploadQueue] Source recording finalized', {
          roomCode,
          uploadPhase,
          uploadId: segment.uploadId,
          expectedChunks,
          cameraLabel: segment.cameraLabel,
          poseName: segment.poseName,
          poseIndex: segment.poseIndex,
        });
        onProgress?.({
          ...(await getQueuedSourceRecordingProgress(roomCode)),
          uploadedSegments,
        });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Source recording upload failed';
      const latest = await getSegment(initialSegment.uploadId);
      if (latest) {
        const resetUploadedProgress = message.startsWith('Failed to finalize source recording');
        await putSegment({
          ...latest,
          status: 'failed',
          uploadedChunks: resetUploadedProgress ? 0 : latest.uploadedChunks,
          uploadedBytes: resetUploadedProgress ? 0 : latest.uploadedBytes,
          errorMessage: message,
        });
      }
      onProgress?.(await getQueuedSourceRecordingProgress(roomCode));
      throw error;
    }
  }

  return getQueuedSourceRecordingProgress(roomCode);
}
